import React, { useState } from "react";

import {
  Box,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIconTypeMap,
} from "@mui/material";
import Lottie from "react-lottie";
import RocketImage from "../assets/gym_login_animation.json";
import { useNavigate } from "react-router-dom";

import Subscriptions from "@mui/icons-material/Subscriptions";
import MembersIcon from "@mui/icons-material/Groups";
import ExercisesIcon from "@mui/icons-material/SelfImprovement";
import WODIcon from "@mui/icons-material/FitnessCenter";
import Logout from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import BackupIcon from "@mui/icons-material/Backup";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";

import Movie from "@mui/icons-material/Movie";
import { useTheme } from "@mui/material/styles";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useAuth } from "../context/AuthContext";
import { ConfirmationDialog } from "../utils/ConfirmationDialog";
import {
  AdminFeatureKey,
  isAdminFeatureEnabled,
} from "../config/adminFeatures";

interface MenuItem {
  label: string;
  Icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string };
  id: number;
  path?: string;
  featureKey?: AdminFeatureKey;
}

const menuItems: Array<MenuItem> = [
  {
    Icon: DashboardIcon,
    label: "Dashboard",
    id: 0,
    path: "/",
    featureKey: "dashboard",
  },
  {
    Icon: MembersIcon,
    label: "Members",
    id: 1,
    path: "members",
    featureKey: "members",
  },
  {
    Icon: ContactPhoneIcon,
    label: "Leads",
    id: 12,
    path: "leads",
    featureKey: "leads",
  },
  {
    Icon: ReceiptIcon,
    label: "Subscriptions & Orders",
    id: 10,
    path: "subscriptions-orders",
    featureKey: "subscriptions_orders",
  },
  {
    Icon: ExercisesIcon,
    label: "Exercises",
    id: 2,
    path: "exercise",
    featureKey: "exercises",
  },
  {
    Icon: WODIcon,
    label: "Workout of the Day",
    id: 3,
    path: "wod",
    featureKey: "wod",
  },
  {
    Icon: ImageSearchIcon,
    label: "Transformation WOD",
    id: 11,
    path: "transformation-wod",
    featureKey: "transformation_wod",
  },
  {
    Icon: RestaurantIcon,
    label: "Deit Plan",
    id: 7,
    path: "dietplans",
    featureKey: "dietplans",
  },
  {
    Icon: Subscriptions,
    label: "Programs",
    id: 4,
    path: "programs",
    featureKey: "programs",
  },
  {
    Icon: Movie,
    label: "Content",
    id: 5,
    path: "content",
    featureKey: "content",
  },
  {
    Icon: BackupIcon,
    label: "Manage App Config",
    id: 8,
    path: "content-management",
    featureKey: "content_management",
  },
  {
    Icon: Logout,
    id: 6,
    label: "Logout",
  },
];

export const Drawer = () => {
  const navigate = useNavigate();
  // let location = useLocation();
  const theme = useTheme();
  const { logoutUser } = useAuth();

  const [selectedMenu, setSelectedMenu] = useState<number>(0);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: RocketImage,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  // useEffect(() => {
  //   console.log("location", location);
  // }, [location]);

  const onMenuItemClick = (menuItem: MenuItem) => {
    if (menuItem.id === 6) {
      setIsLogoutDialogOpen(true);
      return;
    }

    if (menuItem.path) {
      setSelectedMenu(menuItem.id);
      navigate(menuItem.path);
    }
  };

  const visibleMenuItems = menuItems.filter(
    (menuItem) =>
      !menuItem.featureKey || isAdminFeatureEnabled(menuItem.featureKey)
  );

  const handleLogoutConfirm = () => {
    logoutUser();
  };

  return (
    <>
      <Box
        sx={{
          width: 250,
          borderRight: 2,
          borderColor: "#CFCCCC",
        }}
        role="presentation"
      >
        <Grid
          sx={{
            padding: 2,
            display: { xs: "none", sm: "flex", md: "flex" },
          }}
          flexGrow={1}
          display="flex"
          flexDirection="column"
        >
          <Lottie options={defaultOptions} height={200} width={200} />
        </Grid>
        <Divider />
        <List>
          {visibleMenuItems.map((menuItem) => (
            <ListItem key={menuItem.label} disablePadding>
              <ListItemButton onClick={() => onMenuItemClick(menuItem)}>
                <ListItemIcon>
                  {
                    <menuItem.Icon
                      color={menuItem.id === selectedMenu ? "primary" : "info"}
                    />
                  }
                </ListItemIcon>
                <ListItemText
                  sx={{
                    color:
                      menuItem.id === selectedMenu
                        ? theme.palette.primary.main
                        : theme.palette.info.main,
                  }}
                  primary={menuItem.label}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <ConfirmationDialog
        open={isLogoutDialogOpen}
        handleClose={() => setIsLogoutDialogOpen(false)}
        heading="Logout"
        description="Are you sure you want to log out?"
        cancelLabel="Cancel"
        confirmLabel="Logout"
        onProceed={handleLogoutConfirm}
      />
    </>
  );
};
